import { cx } from '@flexbyt/widget-preview';
import type { ReactNode } from 'react';

/**
 * Surfaces and badges.
 *
 * ---------------------------------------------------------------------------
 * A card has to be earned
 * ---------------------------------------------------------------------------
 * The brief rules out "a rounded card around every piece of content" and
 * "endless cards", and it is worth being precise about why, because the fix is
 * not "fewer cards" — it is knowing what a card means.
 *
 * A card says *this is one of several comparable things*. Three widget layouts
 * side by side are comparable, so they are cards. A heading with a paragraph
 * under it is not one of several comparable things; it is a passage of a
 * document, and putting a border around it makes the page a container of
 * containers.
 *
 * So `Card` exists, and most sections should not use it.
 *
 * ---------------------------------------------------------------------------
 * Hairlines, not shadows
 * ---------------------------------------------------------------------------
 * Every surface here is defined by a 1px ring and, at most, `shadow-xs`. The
 * heavy layered drop shadow is the other half of the template look — it makes a
 * page of cards read as a pile of floating objects, and it stops being a signal
 * the moment everything has one.
 *
 * `ring-1` rather than `border`: a ring does not participate in layout, so a
 * bordered and an unbordered card are the same size and a row of them cannot
 * end up one pixel out of alignment.
 */

const TONES = {
  /** On the white page. */
  default: 'bg-white ring-1 ring-ink-150',
  /** On an `ink-25` section, where a white card needs no ring to separate. */
  raised: 'bg-white shadow-xs ring-1 ring-ink-150',
  /** A recess rather than a lift — for a thing being shown, not offered. */
  sunken: 'bg-ink-25 ring-1 ring-ink-100',
  /** On a dark band. */
  inverse: 'bg-white/[0.06] ring-1 ring-white/10',
} as const;

export function Card({
  children,
  tone = 'default',
  className,
  interactive = false,
}: {
  children: ReactNode;
  tone?: keyof typeof TONES;
  className?: string;
  /**
   * Whether the whole card is a target.
   *
   * It lifts two pixels and casts a soft shadow on hover — a `translate`, never
   * a size change: a card that grows under the cursor shifts everything beside
   * it, and on a grid of six that is the page twitching rather than
   * responding. A translate moves the card alone.
   */
  interactive?: boolean;
}) {
  return (
    <div
      className={cx(
        'rounded-xl',
        TONES[tone],
        interactive &&
          'transition-[background-color,box-shadow,translate] duration-[var(--duration-base)] ease-[var(--ease-out-soft)] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-22px_rgba(20,40,120,0.35)] hover:ring-ink-300',
        className
      )}
    >
      {children}
    </div>
  );
}

const BADGE_TONES = {
  /** The default. Quiet enough to sit inside a card without competing. */
  neutral: 'bg-ink-50 text-ink-700 ring-1 ring-ink-150',
  /** For a genuine, checkable fact — "Native Wix app". */
  positive: 'bg-positive-50 text-positive-800 ring-1 ring-positive-200',
  /** On a dark band. */
  inverse: 'bg-white/10 text-white/85 ring-1 ring-white/15',
} as const;

/**
 * A small factual label.
 *
 * `rounded-md`, not a pill. The brief calls out excessive pill-shaped
 * containers, and a badge is one of the few places a pill is defensible — which
 * is exactly why holding the line here matters: once badges are pills, buttons
 * want to be pills, and then the page is made of lozenges.
 *
 * Badges carry facts, not decoration. "Native Wix app" is a badge; "Popular" is
 * a claim wearing a badge's clothes.
 */
export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: keyof typeof BADGE_TONES;
  className?: string;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-micro font-medium',
        BADGE_TONES[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

/**
 * The flexbyt.com frame: a frosted shell with a long blue shadow, holding a
 * product screen the way a screenshot sits on a desk. Wraps the homepage's
 * app and website panels so they read as one family with the hero stage.
 */
export function Frame({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'rounded-[2rem] bg-white/60 p-2 ring-1 ring-black/[0.06] backdrop-blur',
        'shadow-[0_40px_120px_-40px_rgba(20,40,120,0.35)]',
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * The flexbyt.com night stage: a frosted frame around a near-black panel lit
 * by one brand and one grape glow, with grain and a faint grid. For the one or
 * two product moments on a page that should feel like a showcase.
 */
export function NightStage({
  children,
  className,
  innerClassName,
}: {
  children: ReactNode;
  className?: string;
  innerClassName?: string;
}) {
  return (
    <div
      className={cx(
        'relative rounded-[2.5rem] bg-white/60 p-2 ring-1 ring-black/[0.06] backdrop-blur sm:p-3',
        'shadow-[0_40px_120px_-40px_rgba(20,40,120,0.45)]',
        className
      )}
    >
      <div
        className={cx(
          'grain relative overflow-hidden rounded-[2rem] bg-night',
          innerClassName
        )}
      >
        <div
          aria-hidden
          className="grid-fade pointer-events-none absolute inset-0 [--grid-line:rgba(255,255,255,0.05)]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-24 top-10 size-96 rounded-full bg-brand/40 blur-[110px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 bottom-0 size-96 rounded-full bg-grape/45 blur-[110px]"
        />
        {children}
      </div>
    </div>
  );
}
